import React from 'react';
import { getSelectedTariff, tariffPriceWithDiscount } from '@src/domains/tariffs';
import type { TariffsPerType, FullProduct } from '@src/stores/product';
import type { TariffsPerTypeExtended } from '@src/domains/tariffs/core/tariffDisplay';
import { useTranslate } from '@src/core/hooks';
import BasicQuantitySelectorInput from '@src/components/reusable/input/quantitySelector/BasicQuantitySelectorInput';
import TariffVerificationFields from '../ProductAdderTariffVerificationFields';
import {
  TariffsContainer,
  TariffInfoContainer,
  TariffAreaInfo,
  TariffItemContainer,
  TariffItemLabel,
  TariffItemNameLabel,
  TariffItemIsDependentLabel,
  TariffItemValue,
  TariffPrice,
  TariffOldPrice,
  TariffQuantityContainer,
  TariffQuantityLabel,
  TariffSelectInputArea,
  TariffDescription,
  UnavailableTariffLabel,
  ProductAdderErrorLabel,
  RepricingErrorLabel,
} from './styles';

interface ProductAdderTariffListProps {
  tariffs: TariffsPerTypeExtended[];
  product: FullProduct;
  selectedMinutes: number | null;
  repricingByTariffId: Record<string, 'idle' | 'loading' | 'error'>;
  tariffPackageDiscount: number | undefined;
  hasQuantityLimitError: boolean;
  hasRepricingError: boolean;
  priceInfoFor: (tariff: TariffsPerType, useOldPrice?: boolean) => string;
  descriptionFor: (tariff: TariffsPerType) => string;
  isDependent: (tariffTypeId: string) => boolean;
  calculateTariffIndex: (index: number) => number | null;
  onUpdateQuantity: (tariffTypeId: string, quantity: number) => void;
  acceptableQuantitiesFor: (tariff: TariffsPerType) => [number, number];
  showSingleError: (tariffTypeId: string) => boolean;
  showIndependentError: (tariffTypeId: string) => boolean;
  showMultipleError: boolean;
}

const ProductAdderTariffList = (props: ProductAdderTariffListProps) => {
  const { i18n } = useTranslate();

  const {
    tariffs,
    product,
    selectedMinutes,
    repricingByTariffId,
    tariffPackageDiscount,
    hasQuantityLimitError,
    hasRepricingError,
    priceInfoFor,
    descriptionFor,
    isDependent,
    calculateTariffIndex,
    onUpdateQuantity,
    acceptableQuantitiesFor,
    showSingleError,
    showIndependentError,
    showMultipleError,
  } = props;

  return (
    <TariffsContainer>
      {tariffs.map((tariff, index) => {
        const tariffId = getSelectedTariff(tariff, selectedMinutes)?.id;
        const isRepricing = repricingByTariffId[tariffId ?? ''] === 'loading';
        const tariffOldPrice = priceInfoFor(tariff, true);
        const tariffPrice = priceInfoFor(tariff);

        return (
          <div key={tariff.type.id}>
            <TariffInfoContainer isAvailable={tariff.isAvailable} hasError={hasQuantityLimitError}>
              <TariffAreaInfo>
                <TariffItemContainer>
                  <TariffItemLabel>
                    <TariffItemNameLabel>{tariff.type.name}</TariffItemNameLabel>
                    {isDependent(tariff.type.id) ? (
                      <TariffItemIsDependentLabel>{i18n('adder_dependent_tariff')}</TariffItemIsDependentLabel>
                    ) : null}
                  </TariffItemLabel>
                  <TariffItemValue>
                    {isRepricing ? (
                      <TariffPrice aria-busy='true'>{i18n('adder_updating_price')}</TariffPrice>
                    ) : (
                      <>
                        {tariffOldPrice && !tariffPackageDiscount ? (
                          <TariffOldPrice>
                            {i18n('adder_price_from')} <span>{tariffOldPrice}</span>
                          </TariffOldPrice>
                        ) : null}

                        <TariffPrice lineThrough={!!tariffPackageDiscount}>{tariffPrice}</TariffPrice>

                        {tariffPackageDiscount ? (
                          <TariffPrice>{tariffPriceWithDiscount(tariffPrice, tariffPackageDiscount)}</TariffPrice>
                        ) : null}
                      </>
                    )}
                  </TariffItemValue>
                </TariffItemContainer>
                <TariffQuantityContainer>
                  {product && tariff.isAvailable ? (
                    <>
                      <TariffQuantityLabel>{i18n('adder_quantity')}</TariffQuantityLabel>
                      <TariffSelectInputArea>
                        <BasicQuantitySelectorInput
                          value={tariff.selectedQuantity ? tariff.selectedQuantity : 0}
                          setValue={(quantity) => onUpdateQuantity(tariff.type.id, quantity)}
                          defaultRange={[product.minSaleTariffQuantity, product.maxSaleTariffQuantity]}
                          calculatedRange={acceptableQuantitiesFor(tariff)}
                        />
                      </TariffSelectInputArea>
                    </>
                  ) : (
                    <UnavailableTariffLabel>{i18n('adder_unavailableTariffNotice')}</UnavailableTariffLabel>
                  )}
                </TariffQuantityContainer>
              </TariffAreaInfo>

              <TariffDescription>{descriptionFor(tariff)}</TariffDescription>
              <TariffVerificationFields
                tariffTypeId={tariff.type.id}
                quantity={tariff.selectedQuantity}
                tariffTypePosition={calculateTariffIndex(index)}
              />
            </TariffInfoContainer>

            {tariff.isAvailable && showSingleError(tariff.type.id) ? (
              <ProductAdderErrorLabel>{i18n('adder_singleErrorLabel')}</ProductAdderErrorLabel>
            ) : null}

            {showIndependentError(tariff.type.id) ? (
              <ProductAdderErrorLabel>{i18n('adder_dependent_tariff_selection_error')}</ProductAdderErrorLabel>
            ) : null}
          </div>
        );
      })}

      {showMultipleError ? <ProductAdderErrorLabel>{i18n('adder_multipleErrorLabel')}</ProductAdderErrorLabel> : null}

      {hasRepricingError ? <RepricingErrorLabel>{i18n('adder_price_update_failed')}</RepricingErrorLabel> : null}
    </TariffsContainer>
  );
};

export default ProductAdderTariffList;
